import React, { useState, useEffect, Fragment } from "react";
import PropTypes from "prop-types";
import { styled } from "@mui/material/styles";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { Typography } from "@mui/material";
import { Translate, getTranslation } from "orion-components/i18n";

const propTypes = {
	alertActions: PropTypes.array,
	alertCreatedDate: PropTypes.string,
	acknowledged: PropTypes.bool
};

const defaultProps = {
	alertActions: [],
	alertCreatedDate: "",
	acknowledged: false
};

const durationToMilliseconds = (duration, units) => {
	let multiplier = 1000;
	switch (units) {
		case "sec":
			multiplier = 1000;
			break;
		case "min":
			multiplier = multiplier * 60;
			break;
		case "hr":
			multiplier = multiplier * 60 * 60;
			break;
		case "day":
			multiplier = multiplier * 60 * 60 * 24;
			break;
	}
	return duration * multiplier;
};

const HtmlTooltip = styled(({ className, ...props }) => <Tooltip {...props} classes={{ popper: className }} data-testid="orion-components-shared-components-bottom-alert-list-components-alert-timer-cell-tooltip" />)(
	({ theme }) => ({
		[`& .${tooltipClasses.tooltip}`]: {
			backgroundColor: "#f5f5f9",
			color: "rgba(0, 0, 0, 0.87)",
			maxWidth: "none",
			fontSize: theme.typography.pxToRem(12),
			border: "1px solid #dadde9"
		}
	})
);

const getTimerText = (endTime) => {
	const now = new Date().getTime();
	const distance = endTime - now;

	const days = String(Math.floor(Math.abs(distance / (1000 * 60 * 60 * 24)))).padStart(2, "0");
	const hours = String(Math.floor(Math.abs((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)))).padStart(2, "0");
	const minutes = String(Math.floor(Math.abs((distance % (1000 * 60 * 60)) / (1000 * 60)))).padStart(2, "0");
	const seconds = String(Math.floor(Math.abs((distance % (1000 * 60)) / 1000))).padStart(2, "0");
	const timerText = `${distance < 0 ? "+" : "-"}${days > 0 ? `${days}:` : ""}${hours}:${minutes}:${seconds}`;
	return { timerText, distance };
};

const AlertTimerCell = ({ alertActions, alertCreatedDate, acknowledged }) => {
	const [actionTimers, setActionTimers] = useState([]);

	useEffect(() => {
		if (alertActions?.length) {
			const timers = alertActions.filter((action) => action.ackTimerEnabled || action.closeTimerEnabled);
			if (timers?.length) {
				const updateTimers = () => {
					const newTimers = timers.map((action) => {
						const endTime =
							new Date(alertCreatedDate).getTime() +
							durationToMilliseconds(action.actionTimerDuration, action.actionTimerUnit);

						const { timerText, distance } = getTimerText(endTime);

						return { action, timerText, distance };
					});
					setActionTimers(newTimers);
				};
				updateTimers();

				const interval = setInterval(updateTimers, 1000);

				return () => clearInterval(interval);
			}
		}
	}, [alertActions, alertCreatedDate]);

	const upcomingActions = actionTimers.filter((timer) => timer.distance > 0);

	return !acknowledged && upcomingActions.length ? (
		upcomingActions
			.sort((a, b) => a.distance - b.distance)
			.slice(0, 1)
			.map((timer, index) => (
				<HtmlTooltip
					key={`${timer.action.alertActionInstanceId}_${index}`}
					title={
						<React.Fragment>
							{actionTimers.map((timer) => (
								<div
									style={{
										display: "flex",
										justifyContent: "space-between"
									}}
									key={index}
								>
									<Typography style={{ fontSize: "11px", marginRight: 8 }} color="#82878C">
										{timer.action.label}
										{":"}
									</Typography>
									<Typography style={{ fontSize: "11px" }} color="#82878C">
										{timer.action.result?.success ? (
											<div>
												<Translate value="global.sharedComponents.bottomAlarmList.alarmTimerCell.success" />
											</div>
										) : timer.action.result ? (
											<div>
												<Translate value="global.sharedComponents.bottomAlarmList.alarmTimerCell.failed" />
											</div>
										) : (
											timer.timerText
										)}
									</Typography>
								</div>
							))}
						</React.Fragment>
					}
				>
					<p style={{ fontSize: "11px" }}>
						{timer.action.label}: {timer.timerText}
					</p>
				</HtmlTooltip>
			))
	) : (
		<Fragment />
	);
};

AlertTimerCell.propTypes = propTypes;
AlertTimerCell.defaultProps = defaultProps;

export default AlertTimerCell;
